/**
 * Dashboard API helpers — notes, chat, automated activity, extract.
 */
import { api } from './api';

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------
export interface Note {
  id: string;
  orgId: string;
  userId: string;
  title: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
  isOwner: boolean;
  canEdit: boolean;
  sharedCount: number;
}

export interface NoteShare {
  id: string;
  noteId: string;
  userId: string;
  canEdit: boolean;
  createdAt: string;
  email: string;
  firstName: string;
  lastName: string;
}

export const listNotes = (sessionId?: string | null) =>
  api.get<Note[]>('/notes', sessionId);

export const getNote = (id: string, sessionId?: string | null) =>
  api.get<Note>(`/notes/${id}`, sessionId);

export const createNote = (input: { title?: string; body?: string }, sessionId?: string | null) =>
  api.post<Note>('/notes', input, sessionId);

export const patchNote = (id: string, patch: { title?: string | null; body?: string }, sessionId?: string | null) =>
  api.patch<Note>(`/notes/${id}`, patch, sessionId);

export const deleteNote = (id: string, sessionId?: string | null) =>
  api.delete<{ ok: true }>(`/notes/${id}`, sessionId);

export const listNoteShares = (id: string, sessionId?: string | null) =>
  api.get<NoteShare[]>(`/notes/${id}/shares`, sessionId);

export const addNoteShare = (id: string, input: { userId: string; canEdit?: boolean }, sessionId?: string | null) =>
  api.post<{ ok: true }>(`/notes/${id}/shares`, input, sessionId);

export const removeNoteShare = (id: string, userId: string, sessionId?: string | null) =>
  api.delete<{ ok: true }>(`/notes/${id}/shares/${userId}`, sessionId);

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  mode: 'ask' | 'ai_note' | null;
  createdAt: string;
}

export const listChatMessages = (sessionId?: string | null) =>
  api.get<ChatMessage[]>('/chat/dashboard', sessionId);

export const sendChatMessage = (
  input: { content: string; mode?: 'ask' | 'ai_note' },
  sessionId?: string | null,
) => api.post<{ user: ChatMessage; assistant: ChatMessage }>('/chat/dashboard', input, sessionId);

export const clearChatHistory = (sessionId?: string | null) =>
  api.delete<{ ok: true }>('/chat/dashboard', sessionId);

// ---------------------------------------------------------------------------
// Automated activity
// ---------------------------------------------------------------------------
export interface AutomationRun {
  id: string;
  kind: string;
  entityType: string | null;
  entityId: string | null;
  status: 'success' | 'partial' | 'error';
  summary: string | null;
  message: string | null;
  error: string | null;
  sourceAttachmentId: string | null;
  createdAt: string;
}

export const listAutomationRuns = (limit = 20, sessionId?: string | null) =>
  api.get<AutomationRun[]>(`/automated-activity?limit=${limit}`, sessionId);

// ---------------------------------------------------------------------------
// AI Extract (Power-AI pills)
// ---------------------------------------------------------------------------
export interface AccountMatch {
  accountId: string;
  accountName: string;
  confidence: number;
  paymentTerms: string | null;
}

export interface ExtractedCustomerPoLine {
  id: string;
  customerLineNumber: string;
  customerDescription: string | null;
  quantity: string;
  customerUom: string | null;
  unitPrice: string | null;
  extendedAmount: string | null;
  originalParsedQuantity: string | null;
}

export interface ExtractCustomerPoResponse {
  customerPoId: string;
  customerPo: {
    id: string;
    customerPoNumber: string | null;
    status: string;
    source: string;
    accountId: string | null;
    customerBuyerName: string | null;
    customerPoDate: string | null;
    originalParsedShipToRaw: string | null;
    customerInstructions: string | null;
    customerPaymentTerms: string | null;
    parseConfidence: string | null;
    lines: ExtractedCustomerPoLine[];
  };
  accountMatch: AccountMatch | null;
  attachmentId: string;
}

export const extractCustomerPo = (
  input: { storageKey: string; originalFilename: string },
  sessionId?: string | null,
) => api.post<ExtractCustomerPoResponse>('/customer-pos/extract', input, sessionId);

export interface ExtractGenericResponse {
  pending?: boolean;
  accountMatch: AccountMatch | null;
  attachmentId: string;
  message?: string;
  parsed?: unknown;
  vendorQuoteId?: string;
  apInvoiceId?: string;
  goodsReceiptId?: string;
}

export const extractVendorQuote = (input: { storageKey: string; originalFilename: string }, sessionId?: string | null) =>
  api.post<ExtractGenericResponse>('/vendor-quotes/extract', input, sessionId);

export const extractApInvoice = (input: { storageKey: string; originalFilename: string }, sessionId?: string | null) =>
  api.post<ExtractGenericResponse>('/ap-invoices/extract', input, sessionId);

export const extractGoodsReceipt = (input: { storageKey: string; originalFilename: string }, sessionId?: string | null) =>
  api.post<ExtractGenericResponse>('/goods-receipts/extract', input, sessionId);

// ---------------------------------------------------------------------------
// Org members (used by Note share picker) — the /api/team endpoint returns
// memberships with a nested .user; we project that into a simple shape.
// ---------------------------------------------------------------------------
export interface OrgMember {
  id: string;        // user id
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface RawMembership {
  userId: string;
  role: string;
  status: string;
  user: { id: string; email: string; firstName: string; lastName: string };
}

export async function listOrgMembers(sessionId?: string | null): Promise<OrgMember[]> {
  const rows = await api.get<RawMembership[]>('/team', sessionId);
  return (rows || [])
    .filter((r) => r?.status === 'active' && r?.user)
    .map((r) => ({
      id: r.user.id,
      email: r.user.email,
      firstName: r.user.firstName ?? '',
      lastName: r.user.lastName ?? '',
      role: r.role,
    }));
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const dashboardKeys = {
  notes: () => ['dashboard', 'notes'] as const,
  note: (id: string) => ['dashboard', 'notes', id] as const,
  noteShares: (id: string) => ['dashboard', 'notes', id, 'shares'] as const,
  chat: () => ['dashboard', 'chat'] as const,
  automation: () => ['dashboard', 'automation'] as const,
  orgMembers: () => ['dashboard', 'org-members'] as const,
};
